<template>
  <div class="audio-embed">
    <!-- The load failed: what it means to a visitor, never the API's sentence -->
    <PlayerRefusal
      v-if="embedLoader.failure.value"
      :failure="embedLoader.failure.value"
      :can-retry="!!props.embedId"
      @retry="handleRetry"
    />

    <!-- Skeleton: shown until the player is ready for the first time -->
    <div
      v-else-if="!hasBeenReady"
      class="audio-embed__skeleton"
      role="status"
      :aria-label="t('player.status.loading')"
    >
      <div class="audio-embed__skeleton-content">
        <div class="audio-embed__skeleton-cover audio-embed__skeleton-shape" />
        <div class="audio-embed__skeleton-main">
          <div class="audio-embed__skeleton-title audio-embed__skeleton-shape" />
          <div class="audio-embed__skeleton-waveform audio-embed__skeleton-shape" />
        </div>
      </div>
      <div class="audio-embed__skeleton-toolbar">
        <div class="audio-embed__skeleton-play-btn audio-embed__skeleton-shape" />
        <div class="audio-embed__skeleton-bar audio-embed__skeleton-shape" />
      </div>
    </div>

    <!-- Billing gate: owner out of credits -->
    <PlayerUnavailable v-else-if="isBlocked" />

    <!-- Player: stays mounted across track switches to preserve state -->
    <AudioPlayer
      ref="audioPlayerRef"
      v-else-if="playerConfig"
      :config="playerConfig"
      :playback-info="currentPlaybackInfo"
      :show-playlist="showPlaylist"
      :branding-logo-url="brandingLogoUrl"
      :show-branding="showBranding"
      :theme="theme"
      :icon-color="iconColor"
      :seeking="seeking"
      @item-change="handleItemChange"
      @play="$emit('play')"
      @pause="$emit('pause')"
      @ended="$emit('ended')"
      @time-update="$emit('timeUpdate', $event)"
      @duration-change="$emit('durationChange', $event)"
      @volume-change="(volume, muted) => $emit('volumeChange', volume, muted)"
      @playback-speed-change="$emit('playbackSpeedChange', $event)"
      @waveform-load="$emit('waveformLoad', $event)"
      @fullscreen-change="$emit('fullscreenChange', $event)"
      @error="$emit('error', $event)"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, toRef } from 'vue'
import type { PropType } from 'vue'
import type { Embed } from '../../types/embed'
import type { AudioAssetListItem, AudioPlaybackInfo } from '../../types/asset'
import type { AudioPlayerConfig, WaveformData } from '../types'
import type { PlayerError } from '../../types/base'
import type { CollectionItem } from '../../types/player'
import { useEmbedLoader } from '../../common/composables/useEmbedLoader'
import PlayerRefusal from '../../common/components/PlayerRefusal.vue'
import { usePlaybackInfoCache } from '../../common/composables/usePlaybackInfoCache'
import AudioPlayer from './AudioPlayer.vue'
import PlayerUnavailable from '../../common/components/PlayerUnavailable.vue'
import { createUUID } from '../../types/base'
import { useAudioI18n } from '../i18n'

const props = defineProps({
  /** Embed ID to load */
  embedId: {
    type: String,
    default: undefined,
  },
  /** Embed data (if already loaded) */
  embed: {
    type: Object as PropType<Embed<AudioAssetListItem>>,
    default: undefined,
  },
  /** Viewer ID for analytics */
  viewerId: {
    type: String,
    default: undefined,
  },
  /** Auto-play when loaded */
  autoPlay: {
    type: Boolean,
    default: false,
  },
  /** Loop playback */
  loop: {
    type: Boolean,
    default: false,
  },
  /** Auto-play next item in a collection when the current one ends */
  autoPlayNext: {
    type: Boolean,
    default: true,
  },
  /** Shuffle mode */
  shuffle: {
    type: Boolean,
    default: false,
  },
  /** Start muted */
  muted: {
    type: Boolean,
    default: false,
  },
  /** Show waveform visualization */
  showWaveform: {
    type: Boolean,
    default: true,
  },
  /** Show chapters list and markers */
  showChapters: {
    type: Boolean,
    default: true,
  },
  /** Show playlist for collections */
  showPlaylist: {
    type: Boolean,
    default: true,
  },
  /** Enable volume control */
  enableVolume: {
    type: Boolean,
    default: true,
  },
  /** Enable playback speed control */
  enablePlaybackSpeed: {
    type: Boolean,
    default: true,
  },
  /** Enable fullscreen mode */
  enableFullscreen: {
    type: Boolean,
    default: true,
  },
  /** Start playback at this time in seconds */
  startAt: {
    type: Number,
    default: undefined,
  },
  /** Player theme */
  theme: {
    type: String as PropType<'dark' | 'light' | 'colorful'>,
    default: 'colorful',
  },
  /** Custom icon color */
  iconColor: {
    type: String,
    default: undefined,
  },
  /** Prefetch adjacent tracks */
  prefetchAdjacent: {
    type: Boolean,
    default: true,
  },
  /** Branding logo URL */
  brandingLogoUrl: {
    type: String,
    default: undefined,
  },
  /** Show branding (enabled in public embed context only) */
  showBranding: {
    type: Boolean,
    default: false,
  },
  /** Whether the listener may move the playhead (`seeking`) */
  seeking: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits<{
  embedLoad: [embed: Embed<AudioAssetListItem>]
  itemChange: [index: number, item: CollectionItem<AudioAssetListItem>]
  play: []
  pause: []
  ended: []
  timeUpdate: [currentTime: number]
  durationChange: [duration: number]
  volumeChange: [volume: number, muted: boolean]
  playbackSpeedChange: [speed: number]
  waveformLoad: [waveform: WaveformData]
  fullscreenChange: [isFullscreen: boolean]
  error: [error: PlayerError]
}>()

// i18n
const { t } = useAudioI18n()

// Embed loader
const embedIdRef = toRef(props, 'embedId')
const embedPropRef = toRef(props, 'embed')

const embedLoader = useEmbedLoader<AudioAssetListItem>({
  embedId: embedIdRef,
  embed: embedPropRef,
  autoLoad: true,
})

// Playback info cache
const playbackInfoCache = usePlaybackInfoCache<AudioPlaybackInfo>({
  playbackToken: computed(() => embedLoader.embed.value?.playbackToken),
  itemToken: computed(() => embedLoader.embed.value?.itemToken),
  collectionPlaybackId: computed(() => embedLoader.embed.value?.collectionPlaybackId),
  sessionId: computed(() => embedLoader.embed.value?.sessionId),
})

// Current item index for tracking
const currentIndex = ref(0)

// Current playback info
const currentPlaybackInfo = computed<AudioPlaybackInfo | undefined>(() => {
  const embed = embedLoader.embed.value
  if (!embed || embed.items.length === 0) return undefined

  const currentItem = embed.items[currentIndex.value]
  if (!currentItem) return undefined

  return playbackInfoCache.get(currentItem.asset.uid)
})

// Public billing gate: owner out of credits — show a neutral "unavailable" state.
const isBlocked = computed(() => currentPlaybackInfo.value?.blocked === true)

// Player config - derived from embed
const playerConfig = computed<AudioPlayerConfig | null>(() => {
  const embed = embedLoader.embed.value
  if (!embed) return null

  return {
    uid: embed.uid,
    title: embed.name,
    items: embed.items.map((item, index) => ({
      asset: item.asset,
      position: index,
      customTitle: item.customTitle,
      customDescription: item.customDescription,
    })),
    currentIndex: currentIndex.value,
    viewerId: createUUID(props.viewerId || 'viewer'),
    autoPlay: props.autoPlay,
    autoPlayNext: props.autoPlayNext,
    loop: props.loop,
    shuffle: props.shuffle,
    muted: props.muted,
    startAt: props.startAt,
    showWaveform: props.showWaveform,
    showChapters: props.showChapters,
    enableVolume: props.enableVolume,
    enablePlaybackSpeed: props.enablePlaybackSpeed,
    enableFullscreen: props.enableFullscreen,
    chapters: currentPlaybackInfo.value?.chapters?.map(ch => ({
      title: ch.title,
      startTime: ch.startTime,
    })) || [],
    subtitleTracks: currentPlaybackInfo.value?.subtitleTracks || [],
    allowSourceDownload: embed?.playerConfig?.allowSourceDownload ?? false,
    // The token that reaches the item playing: an in-app item's own, else the publication's.
    playbackToken: playbackInfoCache.tokenOf(embed.items[currentIndex.value]?.asset.uid),
  }
})

// Skeleton is only shown while the embed itself is being fetched. As soon as
// playerConfig exists (items are known), we render the real AudioPlayer — it
// has its own placeholder states (cover icon, waveform bars, play-btn spinner)
// while playback info is still loading. This avoids a skeleton→player size jump.
const hasBeenReady = ref(false)
watch(
  () => Boolean(playerConfig.value),
  (ready) => {
    if (ready) hasBeenReady.value = true
  },
  { immediate: true },
)

/**
 * Load playback info for an asset
 */
async function loadPlaybackInfo(assetUid: string): Promise<AudioPlaybackInfo> {
  return playbackInfoCache.fetchOne(assetUid)
}

/**
 * Handle item change - prefetch adjacent tracks
 */
function handleItemChange(index: number, item: CollectionItem<AudioAssetListItem>): void {
  currentIndex.value = index
  emit('itemChange', index, item)

  // Load playback info for the new current item if not cached
  if (!playbackInfoCache.has(item.asset.uid)) {
    loadPlaybackInfo(item.asset.uid).catch(() => {
      // Error will be handled by the player
    })
  }

  // Prefetch adjacent tracks
  if (props.prefetchAdjacent && embedLoader.embed.value) {
    const items = embedLoader.embed.value.items
    const adjacentUids: string[] = []

    if (index > 0) {
      adjacentUids.push(items[index - 1].asset.uid)
    }
    if (index < items.length - 1) {
      adjacentUids.push(items[index + 1].asset.uid)
    }

    // Prefetch in background (don't await)
    if (adjacentUids.length > 0) {
      playbackInfoCache.fetchBatch(adjacentUids).catch(() => {
        // Silently ignore prefetch errors
      })
    }
  }
}

/**
 * Handle retry button click
 */
function handleRetry(): void {
  if (props.embedId) {
    embedLoader.load(props.embedId)
  }
}

// Watch for embed load and prefetch first item
watch(
  () => embedLoader.embed.value,
  async (embed) => {
    if (embed) {
      emit('embedLoad', embed)

      // Load first item's playback info
      if (embed.items.length > 0) {
        try {
          await loadPlaybackInfo(embed.items[0].asset.uid)
        } catch {
          // Error will be handled by the player
        }
      }
    }
  },
  { immediate: true }
)

const audioPlayerRef = ref<InstanceType<typeof AudioPlayer> | null>(null)

// Expose methods for external control
defineExpose({
  embedLoader,
  playbackInfoCache,
  reload: () => props.embedId && embedLoader.load(props.embedId),
  goToIndex: (index: number) => audioPlayerRef.value?.goToIndex(index),
  play: () => audioPlayerRef.value?.play(),
  pause: () => audioPlayerRef.value?.pause(),
  seek: (time: number) => audioPlayerRef.value?.seek(time),
})
</script>

<style lang="scss" scoped>
@use '@/styles/design-tokens' as *;

.audio-embed {
  width: 100%;
  display: flex;
  flex-direction: column;

  // Skeleton: mirrors the AudioPlayer layout to avoid any size/position jump
  // when the real player mounts in place of it.
  &__skeleton {
    width: 100%;
    background: rgb(var(--v-theme-surface));
    border-radius: $radius-lg;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  &__skeleton-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: $space-xl;
    gap: $space-lg;
  }

  &__skeleton-cover {
    width: 120px;
    height: 120px;
    border-radius: $radius-lg;
    flex-shrink: 0;
  }

  &__skeleton-main {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: $space-md;
    align-items: center;
  }

  &__skeleton-title {
    width: 60%;
    max-width: 240px;
    height: 18px;
    border-radius: $radius-sm;
  }

  &__skeleton-waveform {
    width: 100%;
    max-width: 500px;
    height: 72px;
    border-radius: $radius-sm;
  }

  &__skeleton-toolbar {
    display: flex;
    align-items: center;
    gap: $space-md;
    padding: $space-sm $space-lg;
    min-height: 56px;
  }

  &__skeleton-play-btn {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  &__skeleton-bar {
    flex: 1;
    height: 16px;
    border-radius: $radius-sm;
  }

  &__skeleton-shape {
    background: linear-gradient(
      90deg,
      rgba(var(--v-theme-on-surface), 0.04) 0%,
      rgba(var(--v-theme-on-surface), 0.09) 50%,
      rgba(var(--v-theme-on-surface), 0.04) 100%
    );
    background-size: 200% 100%;
    animation: audio-embed-skeleton-shimmer 1.6s ease-in-out infinite;
  }

  @media (min-width: 600px) {
    &__skeleton-content {
      flex-direction: row;
      align-items: center;
      padding: $space-lg $space-xl;
      gap: $space-xl;
    }

    &__skeleton-cover {
      width: 100px;
      height: 100px;
      border-radius: $radius-md;
    }

    &__skeleton-main {
      align-items: flex-start;
      gap: $space-sm;
    }

    &__skeleton-waveform {
      max-width: none;
    }
  }

  @include reduced-motion {
    &__skeleton-shape {
      animation: none;
      background: rgba(var(--v-theme-on-surface), 0.06);
    }
  }
}

@keyframes audio-embed-skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
